import {DrupalNode, DrupalTaxonomyTerm} from "next-drupal";
import {BreadCrumb, Dictionary, DrupalProductNode} from "@/types/hygiena-types";
import {Link} from "@/components/atoms/link";
import {buildBreadCrumbs} from "@/lib/breadcrumbs";
import dynamic from "next/dynamic";
import {useContext} from "react";
import {DictionaryContext} from "@/context/dictionary-context";
import {NodeBenefitSection} from "@/components/templates/nodes/node--benefit_section";
import {FormattedText} from "@/components/formatted-text";
import {DynamicProductGrid} from "@/components/organisms/widget--product-grid";
import SectionHeading from "@/components/organisms/section-heading";
import NodeDetailCard from "@/components/templates/nodes/node--detail-card";
import {useRouter} from "next/router";

const ArticleSlider = dynamic(() => import("../../../organisms/slider").then((mod) => mod.ArticleSlider));
const ProductSlider = dynamic(() => import("../../../organisms/slider").then((mod) => mod.ProductSlider));

export function ContentProductClass ({
  taxonomy_term,
  termChildren,
  termProducts,
  childProducts,
  additionalContent,
}: {
  taxonomy_term: DrupalTaxonomyTerm,
  termChildren: DrupalTaxonomyTerm[],
  childProducts: {[key: string]: DrupalProductNode[]},
  termProducts: DrupalProductNode[],
  additionalContent: {
    children?: DrupalTaxonomyTerm[]
    products?: DrupalProductNode[],
    relatedNews?: DrupalNode[],
    relatedProducts?: DrupalProductNode[],
  };
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {locale, defaultLocale} = useRouter();
  const breadcrumbs = [] as BreadCrumb[];
  const detailSectionHeader = taxonomy_term?.field_product_class_detail_title;
  const detailSectionDesc = taxonomy_term?.field_product_class_detail_desc;
  const detailSectionCards = taxonomy_term?.field_product_class_detail_cards ?? [];


  /**
   * Scroll to the product listing section title.
   *
   * @param id
   */
  function scrollToSection(id: string) {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({behavior: 'smooth'})
    }
  }

  if (taxonomy_term?.parent?.[0]?.id !== "virtual") {
    buildBreadCrumbs(taxonomy_term.parent[0], breadcrumbs);
  }
  if (taxonomy_term?.field_class_product_type_name) {
    buildBreadCrumbs(taxonomy_term.field_class_product_type_name, breadcrumbs);
  }

  /**
   * Fetches the child product class page-link from the provided link or the class page itself.
   *
   * @param term
   */
  function getChildLink(term: DrupalTaxonomyTerm) {
    // If there is a Product Listing Link field; use it.
    if (term?.field_class_product_list_link) {
      return term.field_class_product_list_link;
    }
    // Alternatively; if Link To Class is checked and Link Text is added, use that.
    else if (term?.field_class_link_to_class && term?.path?.alias && term?.field_class_link_text) {
      return {
        uri: term.path.alias,
        title: term?.field_class_link_text,
      }
    }
    return null;
  }

  return (
   <>
     <article className="px-6 lg:px-0">
       {taxonomy_term?.field_class_benefit_section && (
         <NodeBenefitSection node={taxonomy_term.field_class_benefit_section} className={"py-12"}/>
       )}
       {detailSectionCards?.length && detailSectionHeader ? (
         <div className="container mx-auto mb-12">
            <SectionHeading title={detailSectionHeader}>
              {detailSectionDesc?.processed && <FormattedText processed={detailSectionDesc?.processed}/>}
            </SectionHeading>
           <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-8">
             {detailSectionCards?.map(item => (
               <NodeDetailCard key={item.id} node={item}/>
             ))}
           </div>
         </div>
       ): <></>}
       {/* Display products for the term if any exist on it. */}
       {termProducts?.length ? (
         <div key={taxonomy_term.id} className="container mx-auto py-12">
           <div className={"mb-6 flex items-baseline"}>
             <h2 id={taxonomy_term.id}>{taxonomy_term.name}</h2>
             {taxonomy_term?.field_class_product_list_link && (
               <Link href={taxonomy_term?.field_class_product_list_link?.uri  ?? "/"} className="text-primary inline pl-3">{taxonomy_term.field_class_product_list_link.title} <span>&#8594;</span></Link>
             )}
           </div>
           {taxonomy_term?.field_class_product_list_text?.processed && (
             <div className="text-xl font-light">
               <FormattedText processed={taxonomy_term.field_class_product_list_text.processed}/>
             </div>
           )}
           <DynamicProductGrid nodes={termProducts} filters={false} nodeorder={taxonomy_term?.nodeorder}/>
         </div>
       ) : <></>}
       {/* Children will only display when products are available for it */}
       {termChildren.map(child => (
         <div key={child.id} className="container mx-auto py-6 md:py-12">
           <SectionHeading title={child?.field_class_product_list_header ?? child.name} id={child.id} link={getChildLink(child)} className="scroll-m-20">
             {child?.field_class_product_list_text?.processed && (
               <div className="text-xl font-light">
                 <FormattedText processed={child.field_class_product_list_text.processed}/>
               </div>
             )}
           </SectionHeading>
           <DynamicProductGrid nodes={childProducts[child.id] ?? []} filters={false} nodeorder={child?.nodeorder}/>
         </div>
       ))}
       {additionalContent?.relatedProducts?.length ? (
         <div className="container px-6 py-12 md:py-24 mx-auto">
           <SectionHeading title={`${t?.all?.related_products}`}/>
           <ProductSlider products={additionalContent?.relatedProducts}/>
         </div>
       ): <></>}
       {additionalContent?.relatedNews?.length ? (
         <div className="container px-6 py-12 md:py-24 mx-auto">
           <SectionHeading title={`${t?.all?.related_news}`} link={{uri: `${locale != defaultLocale ? `/${locale}` : '' }/news`, title: `${t?.all?.see_all_news}`}}/>
           <ArticleSlider articles={additionalContent?.relatedNews}/>
         </div>
       ): <></>}
     </article>
   </>
  )
}